import { useCallback, MutableRefObject } from 'react';
import { Template, TemplateSection, TemplateField, VisibilityCondition } from '../../_types';
import { getFieldUniqueId } from '../context';

export const useSectionManagement = (
  templateEditable: Template,
  setTemplateEditable: (template: Template) => void,
  trackActivity: () => void,
  triggerAutoSave: (template: Template, note: string) => void,
  collapsedSections: Record<string, boolean>,
  expandSections: (ids: number[]) => void,
  collapseSections: (ids: number[]) => void,
  selectedFieldForEditing: TemplateField | null,
  setSelectedFieldForEditingState: (field: TemplateField | null) => void,
  selectedSectionForEditing: TemplateSection | null,
  setSelectedSectionForEditingState: (section: TemplateSection | null) => void,
  openFieldOptionsId: string | null,
  setOpenFieldOptionsIdState: (id: string | null) => void,
  template: Template,
  confirm: any,
  pendingVisibilitySectionIdsRef: MutableRefObject<Set<number>>
) => {
  const toggleSectionCollapsed = useCallback(
    (sectionId: number) => {
      const sectionIdKey = String(sectionId); // Convert to string for collapse state Record key
      const isCurrentlyCollapsed = collapsedSections[sectionIdKey] || false;

      if (isCurrentlyCollapsed) {
        expandSections([sectionId]); // Pass numeric ID
      } else {
        // If collapsing the section, close any open field options panels in that section
        if (openFieldOptionsId) {
          const section = template.templateSections.find((s) => s.id === sectionId);
          const hasOpenFieldInSection = section?.sectionFields?.some((field) => {
            const fieldUniqueId = getFieldUniqueId(field);
            return openFieldOptionsId === fieldUniqueId;
          });
          if (hasOpenFieldInSection) {
            setOpenFieldOptionsIdState(null);
          }
        }
        collapseSections([sectionId]); // Pass numeric ID
      }
    },
    [collapsedSections, expandSections, collapseSections, openFieldOptionsId, template.templateSections]
  );

  const addSection = useCallback(() => {
    // Track user activity
    trackActivity();

    const tempSectionGuid = `temp-section-${Date.now()}`;

    // Create new section
    const newSection: TemplateSection = {
      id: 0, // Backend will assign new ID when saved
      templateVersionId: parseInt(templateEditable.versionId || '1'),
      sectionGuid: tempSectionGuid, // Backend will assign proper GUID when saved
      parentSectionGuid: null, // New sections don't have a parent
      name: 'New Section',
      effectiveName: 'New Section', // Set effective name same as name for new sections
      description: '',
      effectiveDescription: '', // Set effective description same as description
      createdAt: new Date().toISOString(),
      createdBy: 'current-user',
      updatedAt: new Date().toISOString(),
      updatedBy: 'current-user',
      sectionFields: [],
      collapsed: false,
      displayOrder: templateEditable.templateSections.length + 1,
      isRequired: false,
      effectiveIsRequired: false, // Set effective values for new sections
      isLocked: false,
      effectiveIsLocked: false,
      isRepeatable: false,
      effectiveIsRepeatable: false,
      isDeleted: false, // New sections are not deleted
      effectiveIsDeleted: false,
      parentTemplateSectionId: null,
      parentTemplateSection: null,
      isConditionallyVisible: false,
      effectiveIsConditionallyVisible: false,
      visibilityConditions: [],
    };

    // Update the editable template immediately (optimistic UI)
    const updatedTemplate: Template = {
      ...templateEditable,
      templateSections: [...templateEditable.templateSections, newSection],
    };

    setTemplateEditable(updatedTemplate);

    // Trigger auto-save in the background
    triggerAutoSave(updatedTemplate, 'Added new section');
  }, [templateEditable, trackActivity, triggerAutoSave]);

  const removeSection = useCallback(
    async (sectionId: number) => {
      try {
        // Show confirmation modal
        await confirm({
          title: 'Remove Section',
          description: 'Are you sure you want to remove this section?',
          confirmationText: 'Yes',
          cancellationText: 'No',
          confirmationButtonProps: { variant: 'contained', color: 'error' },
          cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
        });

        // Track user activity
        trackActivity();

        // Check if the section being removed contains the currently selected field
        if (selectedFieldForEditing) {
          // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
          const sectionToRemove = templateEditable.templateSections.find((section) =>
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId
          );
          if (sectionToRemove) {
            const containsSelectedField = sectionToRemove.sectionFields.some((field) =>
              selectedFieldForEditing.id === 0
                ? field.fieldId === selectedFieldForEditing.fieldId && field.id === 0
                : field.id === selectedFieldForEditing.id
            );
            if (containsSelectedField) {
              setSelectedFieldForEditingState(null);
            }
          }
        }

        // Check if the section being removed is currently selected for editing
        // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
        if (selectedSectionForEditing) {
          const matchesSelected =
            sectionId === 0 ? selectedSectionForEditing.id === 0 : selectedSectionForEditing.id === sectionId;
          // When sectionId === 0, also verify the sectionGuid matches (handled in filter below)
          if (matchesSelected) {
            setSelectedSectionForEditingState(null);
          }
        }

        // Create updated template with section soft deleted
        // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
        const softDeletedSections = templateEditable.templateSections.map((section) => {
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            return {
              ...section,
              isDeleted: true,
              effectiveIsDeleted: true,
              // Also mark all fields in the section as deleted
              sectionFields: section.sectionFields.map((field) => ({
                ...field,
                isDeleted: true,
                effectiveIsDeleted: true,
              })),
            };
          }
          return section;
        });

        const updatedTemplate: Template = {
          ...templateEditable,
          templateSections: softDeletedSections,
        };

        // Update UI immediately (optimistic)
        setTemplateEditable(updatedTemplate);

        // Trigger auto-save in background
        triggerAutoSave(updatedTemplate, 'Removed section');
      } catch (error) {
        // User cancelled the confirmation modal, do nothing
        if (error) {
          console.log('Section removal cancelled by user');
        }
      }
    },
    [
      templateEditable,
      selectedFieldForEditing,
      selectedSectionForEditing,
      setSelectedFieldForEditingState,
      setSelectedSectionForEditingState,
      trackActivity,
      triggerAutoSave,
      confirm,
    ]
  );

  // Section editing operations
  const updateSectionTitle = useCallback(
    (sectionId: number, title: string) => {
      // Track user activity for this input change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            const updatedSection = {
              ...section,
              name: title,
              effectiveName: title, // Update effective property for immediate UI feedback
            };
            // Update selected section if it's the one being edited
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated section title');
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave]
  );

  const updateSectionDescription = useCallback(
    (sectionId: number, description: string) => {
      // Track user activity for this input change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            const updatedSection = {
              ...section,
              description,
              effectiveDescription: description, // Update effective property for immediate UI feedback
            };
            // Update selected section if it's the one being edited
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated section description');
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave]
  );

  const updateSectionRequired = useCallback(
    (sectionId: number, required: boolean) => {
      // Track user activity for this change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            const updatedSection = {
              ...section,
              isRequired: required,
              effectiveIsRequired: required, // Update effective property for immediate UI feedback
            };
            // Update selected section if it's the one being edited
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated section required status');
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave]
  );

  const updateSectionLocked = useCallback(
    (sectionId: number, locked: boolean) => {
      // Track user activity for this change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            let restoredVisibilityFields = {};
            if (!locked && section.parentTemplateSectionId !== null) {
              const parentSection = template.parentTemplate?.templateSections.find(
                (s) => s.id === section.parentTemplateSectionId
              );
              if (parentSection) {
                restoredVisibilityFields = {
                  isConditionallyVisible: parentSection.effectiveIsConditionallyVisible,
                  visibilityConditions: parentSection.visibilityConditions ?? [],
                  effectiveIsConditionallyVisible: parentSection.effectiveIsConditionallyVisible,
                };
              }
            }
            const updatedSection = {
              ...section,
              isLocked: locked,
              effectiveIsLocked: locked, // Update effective property for immediate UI feedback
              ...restoredVisibilityFields,
            };
            // Update selected section if it's the one being edited
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated section locked status');
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave, template]
  );

  const updateSectionRepeatable = useCallback(
    (sectionId: number, repeatable: boolean) => {
      // Track user activity for this change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          // Use hybrid matching: sectionGuid for new sections (id === 0), otherwise use id
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            const updatedSection = {
              ...section,
              isRepeatable: repeatable,
              effectiveIsRepeatable: repeatable, // Update effective property for immediate UI feedback
            };
            // Update selected section if it's the one being edited
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated section repeatable status');
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave]
  );

  const updateSectionConditionallyVisible = useCallback(
    (sectionId: number, isConditionallyVisible: boolean) => {
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            const updatedSection = {
              ...section,
              isConditionallyVisible,
              effectiveIsConditionallyVisible: isConditionallyVisible,
              visibilityConditions: section.visibilityConditions ?? [],
            };
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      setTemplateEditable(updatedTemplate);

      const section = updatedTemplate.templateSections.find((s) => s.id === sectionId);
      const conditions = section?.visibilityConditions || [];
      const isComplete =
        conditions.length > 0 &&
        conditions[0].sectionFieldId != null &&
        conditions[0].sectionFieldId !== 0 &&
        String(conditions[0].value).trim() !== '';

      if (isConditionallyVisible) {
        if (isComplete) {
          // Conditions are already complete, so we can save immediately.
          triggerAutoSave(updatedTemplate, 'Updated section visibility');
        } else {
          // Track as pending: visibility is ON but conditions are not yet complete.
          // We cannot auto-save here because the server rejects empty conditions.
          pendingVisibilitySectionIdsRef.current.add(sectionId);
        }
      } else {
        // Switching back to always visible: clear from pending and auto-save.
        pendingVisibilitySectionIdsRef.current.delete(sectionId);
        triggerAutoSave(updatedTemplate, 'Updated section visibility');
      }
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave, pendingVisibilitySectionIdsRef]
  );

  const updateSectionVisibilityConditions = useCallback(
    (sectionId: number, conditions: VisibilityCondition[]) => {
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          const matches =
            sectionId === 0
              ? section.id === 0 && section.sectionGuid === selectedSectionForEditing?.sectionGuid
              : section.id === sectionId;

          if (matches) {
            const updatedSection = {
              ...section,
              visibilityConditions: conditions,
            };
            const selectedMatches = selectedSectionForEditing
              ? selectedSectionForEditing.id === 0
                ? selectedSectionForEditing.sectionGuid === section.sectionGuid
                : selectedSectionForEditing.id === section.id
              : false;
            if (selectedMatches) {
              setSelectedSectionForEditingState(updatedSection);
            }
            return updatedSection;
          }
          return section;
        }),
      };

      setTemplateEditable(updatedTemplate);

      // Only auto-save when the condition is complete (both sectionFieldId and value are set).
      // This holds the save while the user is still picking Field or Value, matching the
      // same pattern used for field options validation.
      const isComplete =
        conditions.length > 0 &&
        conditions[0].sectionFieldId != null &&
        conditions[0].sectionFieldId !== 0 &&
        String(conditions[0].value).trim() !== '';

      if (isComplete) {
        // Conditions are complete and will be saved — no longer pending.
        pendingVisibilitySectionIdsRef.current.delete(sectionId);
        triggerAutoSave(updatedTemplate, 'Updated section visibility conditions');
      }
    },
    [templateEditable, selectedSectionForEditing, setSelectedSectionForEditingState, trackActivity, triggerAutoSave, pendingVisibilitySectionIdsRef]
  );

  return {
    toggleSectionCollapsed,
    addSection,
    removeSection,
    updateSectionTitle,
    updateSectionDescription,
    updateSectionRequired,
    updateSectionLocked,
    updateSectionRepeatable,
    updateSectionConditionallyVisible,
    updateSectionVisibilityConditions,
  };
};
